/**
 * Economic results step: unit economics parameters table + cash flow charts.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { economyTemplatesApi, getEconomyTemplate } from '@/api/economyTemplates';
import { scenarioLayersApi, ScenarioLayer } from '@/api/scenarioLayers';
import {
  EconomicsVariantResult,
  EconomicsYearlyPoint,
  FieldEconomicsParams,
  LayerEconomicsParams,
  scenarioEconomicsApi,
  ScenarioEconomicsCalculateResponse,
} from '@/api/scenarioEconomicsCalc';
import { scenariosApi } from '@/api/scenarios';
import type { ScenarioCurveType } from '@/api/scenarios';
import type { EconomyTemplate } from '@/types/economyTemplate';
import type { EconomyTemplateDetail } from '@/types/economyTemplate';
import {
  attributesByName,
  convertTemplateNumericValue,
  getMismatchedTemplateCurrencies,
  templateUnitConversionNeeded,
} from '@/utils/applyEconomyTemplate';
import {
  ECONOMICS_INJECTION_COST_LABEL,
  getEconomicsAdditionalOilLabel,
  getEconomicsOilPriceLabel,
  getEconomicsTransportLabel,
  getScenarioUnitLabel,
  OIL_TONS_TO_BARRELS,
  SCENARIO_UNIT_TONS,
} from '@/utils/scenarioUnits';

interface ScenarioEconomicResultsStepProps {
  scenarioId: string;
  readOnly?: boolean;
}

type ParamKey = keyof Omit<LayerEconomicsParams, 'layer_id'>;
type LayerParamDrafts = Record<ParamKey, string>;

const STATIC_PARAM_ROWS: { key: ParamKey; label: string }[] = [
  { key: 'capex_production_well', label: 'CAPEX — production well' },
  { key: 'capex_injection_well', label: 'CAPEX — injection well' },
  { key: 'opex_production_well', label: 'OPEX / year — production well' },
  { key: 'opex_injection_well', label: 'OPEX / year — injection well' },
];

function buildParamRows(unitId: number): { key: ParamKey; label: string }[] {
  return [
    ...STATIC_PARAM_ROWS,
    { key: 'oil_price', label: getEconomicsOilPriceLabel(unitId) },
    { key: 'injection_unit_cost', label: ECONOMICS_INJECTION_COST_LABEL },
    { key: 'abandonment_per_well', label: 'Abandonment / well' },
  ];
}

function buildAdditionalParamRows(unitId: number): { key: ParamKey; label: string }[] {
  return [
    { key: 'additional_cost_per_active_well', label: 'Additional / active well / year' },
    { key: 'additional_cost_per_year', label: 'Additional / year' },
    { key: 'additional_cost_per_oil_unit', label: getEconomicsAdditionalOilLabel(unitId) },
  ];
}

const OPTIONAL_LAYER_PARAM_KEYS = new Set<ParamKey>([
  'additional_cost_per_active_well',
  'additional_cost_per_year',
  'additional_cost_per_oil_unit',
]);

const CURRENCIES = ['USD', 'EUR', 'GBP'];

const VARIANT_ORDER: ScenarioCurveType[] = ['ml', 'p10', 'p50', 'p90'];

const VARIANT_LABELS: Record<ScenarioCurveType, string> = {
  ml: 'ML',
  p10: 'P10',
  p50: 'P50',
  p90: 'P90',
};

function variantLabel(variant: ScenarioCurveType, mode: 'ml' | 'manual'): string {
  if (variant === 'ml') return mode === 'manual' ? 'Base' : 'ML';
  return VARIANT_LABELS[variant];
}

const emptyLayerDrafts = (): LayerParamDrafts => ({
  capex_production_well: '',
  capex_injection_well: '',
  opex_production_well: '',
  opex_injection_well: '',
  oil_price: '',
  injection_unit_cost: '',
  abandonment_per_well: '',
  additional_cost_per_active_well: '',
  additional_cost_per_year: '',
  additional_cost_per_oil_unit: '',
});

function formatMoney(value: number, currency: string): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.length === 3 ? currency : 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

const AXIS_MONEY_LOCALE = 'en-US';

/** Shorter labels for Y-axis ticks (e.g. $1.2M instead of $1,200,000). */
function formatMoneyAxis(value: number, currency: string): string {
  if (!Number.isFinite(value)) return '—';
  const currencyCode = currency.length === 3 ? currency : 'USD';
  const abs = Math.abs(value);
  if (abs === 0) {
    return new Intl.NumberFormat(AXIS_MONEY_LOCALE, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(0);
  }
  return new Intl.NumberFormat(AXIS_MONEY_LOCALE, {
    style: 'currency',
    currency: currencyCode,
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: abs >= 1_000_000 ? 1 : abs >= 10_000 ? 0 : 1,
  }).format(value);
}

function estimateYAxisWidth(rows: ReturnType<typeof toChartRows>, currency: string): number {
  let maxAbs = 0;
  for (const row of rows) {
    maxAbs = Math.max(
      maxAbs,
      Math.abs(row.revenue),
      Math.abs(row.cumulative_cash_flow),
      Math.abs(row.capex),
      Math.abs(row.opex),
      Math.abs(row.injection_cost),
      Math.abs(row.abandonment),
      Math.abs(row.additional_cost),
      Math.abs(row.tax),
    );
  }
  const negativeStack = rows.map(
    (row) =>
      Math.abs(row.capex) +
      Math.abs(row.opex) +
      Math.abs(row.injection_cost) +
      Math.abs(row.abandonment) +
      Math.abs(row.additional_cost) +
      Math.abs(row.tax),
  );
  if (negativeStack.length) {
    maxAbs = Math.max(maxAbs, ...negativeStack);
  }
  const sample = formatMoneyAxis(maxAbs || 1, currency);
  return Math.min(80, Math.max(52, sample.length * 6.5 + 14));
}

function parseNumericInput(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') {
    return 0;
  }
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/** Empty input → null; invalid number → null (caller should validate). */
function parseOptionalNumericInput(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') {
    return null;
  }
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

function numToDraft(value: number | undefined): string {
  if (value == null || value === 0) return '';
  return String(value);
}

function optionalNumToDraft(value: number | null | undefined): string {
  if (value == null) return '';
  return String(value);
}

function rateToPctDraft(rate: number | undefined): string {
  if (rate == null || rate === 0) return '';
  return String(rate * 100);
}

function optionalRateToPctDraft(rate: number | null | undefined): string {
  if (rate == null) return '';
  return String(rate * 100);
}

function layerParamsToDrafts(params: LayerEconomicsParams): LayerParamDrafts {
  return {
    capex_production_well: numToDraft(params.capex_production_well),
    capex_injection_well: numToDraft(params.capex_injection_well),
    opex_production_well: numToDraft(params.opex_production_well),
    opex_injection_well: numToDraft(params.opex_injection_well),
    oil_price: numToDraft(params.oil_price),
    injection_unit_cost: numToDraft(params.injection_unit_cost),
    abandonment_per_well: numToDraft(params.abandonment_per_well),
    additional_cost_per_active_well: optionalNumToDraft(params.additional_cost_per_active_well),
    additional_cost_per_year: optionalNumToDraft(params.additional_cost_per_year),
    additional_cost_per_oil_unit: optionalNumToDraft(params.additional_cost_per_oil_unit),
  };
}

function toChartRows(points: EconomicsYearlyPoint[]) {
  return points.map((pt) => ({
    year: pt.year,
    revenue: pt.revenue,
    capex: -pt.capex,
    opex: -(pt.opex + (pt.fixed_field_opex ?? 0)),
    injection_cost: -pt.injection_cost,
    abandonment: -(pt.abandonment ?? 0),
    additional_cost: -(pt.additional_cost ?? 0),
    tax: -(pt.tax ?? 0),
    cash_flow: pt.cash_flow,
    cumulative_cash_flow: pt.cumulative_cash_flow,
  }));
}

interface CashFlowChartProps {
  title: string;
  subtitle?: string;
  data: EconomicsYearlyPoint[];
  currency: string;
  npv?: number;
}

const ReadOnlyField: React.FC<{ label: string; value: string; minWidth?: number }> = ({
  label,
  value,
  minWidth = 140,
}) => (
  <Box sx={{ minWidth }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

const CashFlowChart: React.FC<CashFlowChartProps> = ({ title, subtitle, data, currency, npv }) => {
  const chartData = useMemo(() => toChartRows(data), [data]);
  const yAxisWidth = useMemo(() => estimateYAxisWidth(chartData, currency), [chartData, currency]);

  if (!chartData.length) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6">{title}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {npv != null && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              NPV: {formatMoney(npv, currency)}
            </Typography>
          )}
        </Box>
        <Box sx={{ width: '100%', minWidth: 0, height: 360 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: 16, left: 4, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis
                width={yAxisWidth}
                tick={{ fontSize: 11 }}
                tickMargin={4}
                tickFormatter={(v) => formatMoneyAxis(v, currency)}
              />
            <Tooltip
              formatter={(value: number, name: string) => [formatMoney(Math.abs(value), currency), name]}
              labelFormatter={(year) => `Year ${year}`}
            />
            <Legend />
            <Bar dataKey="revenue" name="Net revenue" fill="#2e7d32" stackId="cf" />
            <Bar dataKey="capex" name="CAPEX" fill="#c62828" stackId="cf" />
            <Bar dataKey="opex" name="OPEX" fill="#ef6c00" stackId="cf" />
            <Bar dataKey="injection_cost" name="Injection" fill="#1565c0" stackId="cf" />
            <Bar dataKey="abandonment" name="Abandonment" fill="#5d4037" stackId="cf" />
            <Bar dataKey="additional_cost" name="Additional" fill="#00838f" stackId="cf" />
            <Bar dataKey="tax" name="Tax" fill="#6a1b9a" stackId="cf" />
            <Line
              type="monotone"
              dataKey="cumulative_cash_flow"
              name="Cumulative cash flow"
              stroke="#6a1b9a"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export const ScenarioEconomicResultsStep: React.FC<ScenarioEconomicResultsStepProps> = ({
  scenarioId,
  readOnly = false,
}) => {
  const [layers, setLayers] = useState<ScenarioLayer[]>([]);
  const [loadingLayers, setLoadingLayers] = useState(true);
  const [calculationsUpToDate, setCalculationsUpToDate] = useState<boolean | null>(null);
  const [economicsUpToDate, setEconomicsUpToDate] = useState<boolean | null>(null);
  const [unitMatches, setUnitMatches] = useState(true);
  const [economicsUnitId, setEconomicsUnitId] = useState<number | null>(null);
  const [hasSavedEconomics, setHasSavedEconomics] = useState(false);
  const [draftsByLayer, setDraftsByLayer] = useState<Record<string, LayerParamDrafts>>({});
  const [scenarioUnitId, setScenarioUnitId] = useState<number>(SCENARIO_UNIT_TONS);
  const [scenarioMode, setScenarioMode] = useState<'ml' | 'manual'>('ml');
  const [discountRatePct, setDiscountRatePct] = useState('10');
  const [currency, setCurrency] = useState('USD');
  const [royaltyPct, setRoyaltyPct] = useState('');
  const [transportPerUnit, setTransportPerUnit] = useState('');
  const [incomeTaxPct, setIncomeTaxPct] = useState('');
  const [fixedFieldOpex, setFixedFieldOpex] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScenarioEconomicsCalculateResponse | null>(null);
  const [fieldTemplateId, setFieldTemplateId] = useState<string | null>(null);
  const [layerTemplateId, setLayerTemplateId] = useState<string | null>(null);
  const [fieldTemplateOptions, setFieldTemplateOptions] = useState<EconomyTemplate[]>([]);
  const [layerTemplateOptions, setLayerTemplateOptions] = useState<EconomyTemplate[]>([]);
  const [fieldTemplateDetail, setFieldTemplateDetail] = useState<EconomyTemplateDetail | null>(null);
  const [layerTemplateDetail, setLayerTemplateDetail] = useState<EconomyTemplateDetail | null>(null);
  const [templateApplyNote, setTemplateApplyNote] = useState<string | null>(null);

  const paramRows = useMemo(() => buildParamRows(scenarioUnitId), [scenarioUnitId]);
  const additionalParamRows = useMemo(() => buildAdditionalParamRows(scenarioUnitId), [scenarioUnitId]);
  const allParamRows = useMemo(
    () => [...paramRows, ...additionalParamRows],
    [paramRows, additionalParamRows],
  );

  const paramColumnSx = {
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    width: '1%',
    pr: 2,
    verticalAlign: 'top' as const,
  };

  const layerHeaderCellSx = (layerIndex: number) => ({
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    verticalAlign: 'bottom' as const,
    width: 1,
    pl: layerIndex === 0 ? 1 : 0.25,
    pr: 0.25,
    py: 0.75,
  });

  const layerValueCellSx = (layerIndex: number) => ({
    verticalAlign: 'top' as const,
    width: 1,
    pl: layerIndex === 0 ? 1 : 0.25,
    pr: 0.25,
    py: 0.5,
  });

  const economicsTableSx = {
    width: 'max-content',
    tableLayout: 'auto' as const,
  };

  const mismatchedTemplateCurrencies = useMemo(
    () => getMismatchedTemplateCurrencies(currency, fieldTemplateDetail, layerTemplateDetail),
    [currency, fieldTemplateDetail, layerTemplateDetail],
  );

  const buildTemplateApplyNote = useCallback(
    (fieldDetail: EconomyTemplateDetail | null, layerDetail: EconomyTemplateDetail | null) => {
      const notes: string[] = [];
      if (fieldDetail && templateUnitConversionNeeded(fieldDetail.unit_id, scenarioUnitId)) {
        notes.push(
          `Field template units (${getScenarioUnitLabel(fieldDetail.unit_id)}) converted to ${getScenarioUnitLabel(scenarioUnitId)}.`,
        );
      }
      if (layerDetail && templateUnitConversionNeeded(layerDetail.unit_id, scenarioUnitId)) {
        notes.push(
          `Layer template units (${getScenarioUnitLabel(layerDetail.unit_id)}) converted to ${getScenarioUnitLabel(scenarioUnitId)}.`,
        );
      }
      return notes.length ? notes.join(' ') : null;
    },
    [scenarioUnitId],
  );

  const applyFieldTemplateValues = useCallback(
    (detail: EconomyTemplateDetail) => {
      const byName = attributesByName(detail);

      if (byName.discount_rate) {
        setDiscountRatePct(rateToPctDraft(Number(byName.discount_rate)) || '10');
      }
      if (byName.royalty_rate) {
        setRoyaltyPct(optionalRateToPctDraft(Number(byName.royalty_rate)));
      }
      if (byName.income_tax_rate) {
        setIncomeTaxPct(optionalRateToPctDraft(Number(byName.income_tax_rate)));
      }
      if (byName.transport_per_unit) {
        const raw = convertTemplateNumericValue(
          'transport_per_unit',
          byName.transport_per_unit,
          detail.unit_id,
          scenarioUnitId,
        );
        setTransportPerUnit(optionalNumToDraft(Number(raw)));
      }
      if (byName.fixed_field_opex_per_year) {
        setFixedFieldOpex(optionalNumToDraft(Number(byName.fixed_field_opex_per_year)));
      }
    },
    [scenarioUnitId],
  );

  const applyLayerTemplateValues = useCallback(
    (detail: EconomyTemplateDetail | null) => {
      if (!detail) return;

      const byName = attributesByName(detail);
      const layerKeys = Object.keys(emptyLayerDrafts()) as ParamKey[];
      const nextDrafts: Record<string, LayerParamDrafts> = {};

      for (const layer of layers) {
        const drafts = emptyLayerDrafts();
        for (const key of layerKeys) {
          const raw = byName[key];
          if (raw == null || raw === '') continue;
          const converted = convertTemplateNumericValue(key, raw, detail.unit_id, scenarioUnitId);
          if (OPTIONAL_LAYER_PARAM_KEYS.has(key)) {
            drafts[key] = optionalNumToDraft(Number(converted));
          } else {
            drafts[key] = numToDraft(Number(converted));
          }
        }
        nextDrafts[layer.scenario_layer_id] = drafts;
      }
      setDraftsByLayer(nextDrafts);
    },
    [scenarioUnitId, layers],
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoadingLayers(true);
      setError(null);
      try {
        const [layersData, prodStatus, scenario, paramsRes, econStatus, resultsRes] = await Promise.all([
          scenarioLayersApi.getScenarioLayers(scenarioId),
          scenariosApi.getCalculationsStatus(scenarioId),
          scenariosApi.getScenario(scenarioId),
          scenarioEconomicsApi.getParams(scenarioId),
          scenarioEconomicsApi.getStatus(scenarioId),
          scenarioEconomicsApi.getResults(scenarioId).catch(() => null),
        ]);
        if (cancelled) return;
        const sorted = [...layersData].sort((a, b) => a.layer_name.localeCompare(b.layer_name));
        setLayers(sorted);
        setScenarioUnitId(scenario.unit_id ?? SCENARIO_UNIT_TONS);
        setScenarioMode(scenario.mode === 'manual' ? 'manual' : 'ml');
        setCalculationsUpToDate(prodStatus.is_up_to_date);
        setEconomicsUpToDate(econStatus.is_up_to_date);
        setUnitMatches(econStatus.unit_matches);
        setEconomicsUnitId(econStatus.economics_unit_id ?? null);
        setHasSavedEconomics(econStatus.has_results);

        const canShowSavedEconomics =
          !readOnly ||
          (econStatus.has_results &&
            econStatus.is_up_to_date &&
            econStatus.unit_matches &&
            prodStatus.is_up_to_date);

        if (canShowSavedEconomics) {
          const paramsByLayer = new Map(paramsRes.layers.map((p) => [p.layer_id, p]));
          setDiscountRatePct(rateToPctDraft(paramsRes.field.discount_rate) || '10');
          setCurrency(paramsRes.field.currency || 'USD');
          setRoyaltyPct(optionalRateToPctDraft(paramsRes.field.royalty_rate));
          setTransportPerUnit(optionalNumToDraft(paramsRes.field.transport_per_unit));
          setIncomeTaxPct(optionalRateToPctDraft(paramsRes.field.income_tax_rate));
          setFixedFieldOpex(optionalNumToDraft(paramsRes.field.fixed_field_opex_per_year));
          setFieldTemplateId(paramsRes.field.field_economy_template_id ?? null);

          const nextDrafts: Record<string, LayerParamDrafts> = {};
          sorted.forEach((layer) => {
            const saved = paramsByLayer.get(layer.scenario_layer_id);
            nextDrafts[layer.scenario_layer_id] = saved
              ? layerParamsToDrafts(saved)
              : emptyLayerDrafts();
          });
          setDraftsByLayer(nextDrafts);
          const firstLayerTemplate = paramsRes.layers.find((p) => p.layer_economy_template_id);
          setLayerTemplateId(firstLayerTemplate?.layer_economy_template_id ?? null);

          if (resultsRes) {
            setResult(resultsRes);
          } else {
            setResult(null);
          }

          if (!readOnly) {
            const templateLoads: Promise<void>[] = [];
            if (paramsRes.field.field_economy_template_id) {
              templateLoads.push(
                getEconomyTemplate(paramsRes.field.field_economy_template_id)
                  .then((detail) => setFieldTemplateDetail(detail))
                  .catch(() => setFieldTemplateDetail(null)),
              );
            } else {
              setFieldTemplateDetail(null);
            }
            const layerTemplateIdFromParams = paramsRes.layers.find(
              (p) => p.layer_economy_template_id,
            )?.layer_economy_template_id;
            if (layerTemplateIdFromParams) {
              templateLoads.push(
                getEconomyTemplate(layerTemplateIdFromParams)
                  .then((detail) => setLayerTemplateDetail(detail))
                  .catch(() => setLayerTemplateDetail(null)),
              );
            } else {
              setLayerTemplateDetail(null);
            }
            await Promise.all(templateLoads);
          }
        } else if (readOnly) {
          setDiscountRatePct('');
          setCurrency('');
          setRoyaltyPct('');
          setTransportPerUnit('');
          setIncomeTaxPct('');
          setFixedFieldOpex('');
          setFieldTemplateId(null);
          setLayerTemplateId(null);

          const emptyDrafts: Record<string, LayerParamDrafts> = {};
          sorted.forEach((layer) => {
            emptyDrafts[layer.scenario_layer_id] = emptyLayerDrafts();
          });
          setDraftsByLayer(emptyDrafts);
          setResult(null);
        } else {
          const paramsByLayer = new Map(paramsRes.layers.map((p) => [p.layer_id, p]));
          setDiscountRatePct(rateToPctDraft(paramsRes.field.discount_rate) || '10');
          setCurrency(paramsRes.field.currency || 'USD');
          setRoyaltyPct(optionalRateToPctDraft(paramsRes.field.royalty_rate));
          setTransportPerUnit(optionalNumToDraft(paramsRes.field.transport_per_unit));
          setIncomeTaxPct(optionalRateToPctDraft(paramsRes.field.income_tax_rate));
          setFixedFieldOpex(optionalNumToDraft(paramsRes.field.fixed_field_opex_per_year));
          setFieldTemplateId(paramsRes.field.field_economy_template_id ?? null);

          const nextDrafts: Record<string, LayerParamDrafts> = {};
          sorted.forEach((layer) => {
            const saved = paramsByLayer.get(layer.scenario_layer_id);
            nextDrafts[layer.scenario_layer_id] = saved
              ? layerParamsToDrafts(saved)
              : emptyLayerDrafts();
          });
          setDraftsByLayer(nextDrafts);
          const firstLayerTemplate = paramsRes.layers.find((p) => p.layer_economy_template_id);
          setLayerTemplateId(firstLayerTemplate?.layer_economy_template_id ?? null);

          if (resultsRes) {
            setResult(resultsRes);
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load scenario layers');
        }
      } finally {
        if (!cancelled) {
          setLoadingLayers(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [scenarioId, readOnly]);

  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    const loadTemplates = async () => {
      try {
        const [fieldRes, layerRes] = await Promise.all([
          economyTemplatesApi.getTemplates({ template_type: 'field', limit: 100, include_user_names: false }),
          economyTemplatesApi.getTemplates({ template_type: 'layer', limit: 100, include_user_names: false }),
        ]);
        if (!cancelled) {
          setFieldTemplateOptions(fieldRes.templates);
          setLayerTemplateOptions(layerRes.templates);
        }
      } catch {
        // non-blocking
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [readOnly]);

  const applyFieldTemplate = async (template: EconomyTemplate | null) => {
    if (!template) {
      setFieldTemplateId(null);
      setFieldTemplateDetail(null);
      setTemplateApplyNote(buildTemplateApplyNote(null, layerTemplateDetail));
      return;
    }
    try {
      const detail = await getEconomyTemplate(template.id);
      setFieldTemplateId(detail.id);
      setFieldTemplateDetail(detail);
      applyFieldTemplateValues(detail);
      if (layerTemplateDetail) {
        applyLayerTemplateValues(layerTemplateDetail);
      }
      setTemplateApplyNote(buildTemplateApplyNote(detail, layerTemplateDetail));
    } catch {
      setError('Failed to apply field template');
    }
  };

  const applyLayerTemplate = async (template: EconomyTemplate | null) => {
    if (!template) {
      setLayerTemplateId(null);
      setLayerTemplateDetail(null);
      setTemplateApplyNote(buildTemplateApplyNote(fieldTemplateDetail, null));
      return;
    }
    try {
      const detail = await getEconomyTemplate(template.id);
      setLayerTemplateId(detail.id);
      setLayerTemplateDetail(detail);
      if (fieldTemplateDetail) {
        applyFieldTemplateValues(fieldTemplateDetail);
      }
      applyLayerTemplateValues(detail);
      setTemplateApplyNote(buildTemplateApplyNote(fieldTemplateDetail, detail));
    } catch {
      setError('Failed to apply layer template');
    }
  };

  const updateDraft = useCallback((layerId: string, key: ParamKey, raw: string) => {
    setDraftsByLayer((prev) => ({
      ...prev,
      [layerId]: {
        ...(prev[layerId] ?? emptyLayerDrafts()),
        [key]: raw,
      },
    }));
  }, []);

  const buildFieldParams = (): FieldEconomicsParams | null => {
    const discount = Number.parseFloat(discountRatePct.trim().replace(',', '.'));
    if (!Number.isFinite(discount) || discount < 0 || discount > 100) {
      setError('Discount rate must be between 0 and 100 (%)');
      return null;
    }
    const royaltyRate = parseOptionalPercentInput(royaltyPct, 'Royalty');
    if (royaltyRate === false) return null;
    const incomeTaxRate = parseOptionalPercentInput(incomeTaxPct, 'Income tax');
    if (incomeTaxRate === false) return null;

    const transportRaw = transportPerUnit.trim();
    let transport: number | null = null;
    if (transportRaw !== '') {
      transport = parseOptionalNumericInput(transportPerUnit);
      if (transport === null) {
        setError('Transport cost must be a valid number');
        return null;
      }
      if (transport < 0) {
        setError('Transport cost must be zero or positive');
        return null;
      }
    }

    const fixedOpexRaw = fixedFieldOpex.trim();
    let fixedOpex: number | null = null;
    if (fixedOpexRaw !== '') {
      fixedOpex = parseOptionalNumericInput(fixedFieldOpex);
      if (fixedOpex === null) {
        setError('Fixed field OPEX must be a valid number');
        return null;
      }
      if (fixedOpex < 0) {
        setError('Fixed field OPEX must be zero or positive');
        return null;
      }
    }

    return {
      discount_rate: discount / 100,
      currency,
      royalty_rate: royaltyRate,
      transport_per_unit: transport,
      income_tax_rate: incomeTaxRate,
      fixed_field_opex_per_year: fixedOpex,
      field_economy_template_id: fieldTemplateId,
    };
  };

  const buildLayerPayload = (): LayerEconomicsParams[] | null => {
    const payload: LayerEconomicsParams[] = [];

    for (const layer of layers) {
      const drafts = draftsByLayer[layer.scenario_layer_id] ?? emptyLayerDrafts();
      const parsed: Partial<LayerEconomicsParams> = { layer_id: layer.scenario_layer_id };

      for (const row of allParamRows) {
        const raw = drafts[row.key] ?? '';
        if (OPTIONAL_LAYER_PARAM_KEYS.has(row.key)) {
          if (raw.trim() === '') {
            parsed[row.key] = null;
            continue;
          }
          const value = parseOptionalNumericInput(raw);
          if (value === null) {
            setError(`Invalid number for "${row.label}" (${layer.layer_name})`);
            return null;
          }
          if (value < 0) {
            setError(`"${row.label}" (${layer.layer_name}) must be zero or positive`);
            return null;
          }
          parsed[row.key] = value;
          continue;
        }

        const value = parseNumericInput(raw);
        if (value === null) {
          setError(`Invalid number for "${row.label}" (${layer.layer_name})`);
          return null;
        }
        if (value < 0) {
          setError(`"${row.label}" (${layer.layer_name}) must be zero or positive`);
          return null;
        }
        parsed[row.key] = value;
      }

      parsed.layer_economy_template_id = layerTemplateId;
      payload.push(parsed as LayerEconomicsParams);
    }

    return payload;
  };

  const parseOptionalPercentInput = (raw: string, label: string): number | null | false => {
    const trimmed = raw.trim().replace(',', '.');
    if (trimmed === '') {
      return null;
    }
    const value = Number(trimmed);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      setError(`${label} must be between 0 and 100 (%)`);
      return false;
    }
    return value / 100;
  };

  const handleCalculate = async () => {
    setError(null);
    if (layers.length === 0) {
      setError('No reservoirs in scenario');
      return;
    }

    const field = buildFieldParams();
    if (!field) return;

    const layerPayload = buildLayerPayload();
    if (!layerPayload) {
      return;
    }

    try {
      setCalculating(true);
      const response = await scenarioEconomicsApi.calculate(scenarioId, {
        field,
        layers: layerPayload,
      });
      setResult(response);
      setHasSavedEconomics(true);
      setEconomicsUpToDate(true);
      setUnitMatches(true);
      setEconomicsUnitId(scenarioUnitId);
    } catch (err: any) {
      const detail = err?.response?.data?.detail ?? err?.detail ?? err?.message;
      setError(typeof detail === 'string' ? detail : 'Failed to calculate economics');
      setResult(null);
    } finally {
      setCalculating(false);
    }
  };

  const sortedVariants = useMemo(() => {
    if (!result?.variants?.length) {
      return [] as EconomicsVariantResult[];
    }
    const byVariant = new Map(result.variants.map((v) => [v.variant, v]));
    const order = scenarioMode === 'manual' ? (['ml'] as ScenarioCurveType[]) : VARIANT_ORDER;
    return order.map((variant) => byVariant.get(variant)).filter(
      (v): v is EconomicsVariantResult => v != null,
    );
  }, [result, scenarioMode]);

  const layerNames = useMemo(() => {
    const first = sortedVariants[0];
    if (!first) return [];
    return first.layers.map((l) => l.layer_name);
  }, [sortedVariants]);

  const economicsDisplayValid = useMemo(() => {
    if (!readOnly) return true;
    return (
      hasSavedEconomics &&
      economicsUpToDate !== false &&
      unitMatches &&
      calculationsUpToDate !== false
    );
  }, [readOnly, hasSavedEconomics, economicsUpToDate, unitMatches, calculationsUpToDate]);

  const showSavedResults = Boolean(
    result && sortedVariants.length > 0 && unitMatches && economicsDisplayValid,
  );

  if (loadingLayers) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Economic results
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Field-level royalty, transport, tax, and fixed OPEX apply to the combined field cash flow.
        Abandonment is set per reservoir and charged when each well is shut down.
        Optional additional costs (per active well, per year, per oil unit) can be left empty — they count as zero.
      </Typography>

      {hasSavedEconomics && !unitMatches && economicsUnitId != null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Scenario units changed from {getScenarioUnitLabel(economicsUnitId)} to{' '}
          {getScenarioUnitLabel(scenarioUnitId)} since the last economics run. Saved results are
          invalid — update oil price, transport, and per-oil additional costs for the new units
          (1 t = {OIL_TONS_TO_BARRELS} bbl), then recalculate economics.
        </Alert>
      )}

      {hasSavedEconomics && !unitMatches && economicsUnitId == null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Scenario units no longer match the units used for the saved economics run. Update
          per-oil prices for the current units (1 t = {OIL_TONS_TO_BARRELS} bbl), then recalculate
          economics.
        </Alert>
      )}

      {calculationsUpToDate === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {readOnly
            ? 'Production forecast is outdated — layer or well inputs changed after the last production run.'
            : 'Production forecast is outdated. Re-run production calculations before updating economics.'}
        </Alert>
      )}

      {hasSavedEconomics && economicsUpToDate === false && unitMatches && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {readOnly
            ? 'Saved economic results are outdated relative to the current production forecast.'
            : 'Saved economic results are outdated. Re-run production if needed, then recalculate economics.'}
        </Alert>
      )}

      {readOnly && !economicsDisplayValid && hasSavedEconomics && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Saved parameters and results are hidden because the scenario needs a production or economics
          recalculation.
        </Alert>
      )}

      {readOnly && !hasSavedEconomics && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No economic calculation has been saved for this scenario yet.
        </Alert>
      )}

      {templateApplyNote && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setTemplateApplyNote(null)}>
          {templateApplyNote}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Scenario parameters
          </Typography>

          {!readOnly && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 280 }}
                options={fieldTemplateOptions}
                getOptionLabel={(opt) => opt.name}
                value={fieldTemplateOptions.find((t) => t.id === fieldTemplateId) ?? null}
                onChange={(_, value) => {
                  void applyFieldTemplate(value);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Field economics template" placeholder="Select template…" />
                )}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            {readOnly ? (
              <>
                <ReadOnlyField label="Discount rate (%)" value={discountRatePct} minWidth={160} />
                <ReadOnlyField label="Currency" value={currency} minWidth={120} />
                <ReadOnlyField label="Royalty (%)" value={royaltyPct} minWidth={140} />
                <ReadOnlyField
                  label={getEconomicsTransportLabel(scenarioUnitId)}
                  value={transportPerUnit}
                  minWidth={160}
                />
                <ReadOnlyField label="Income tax (%)" value={incomeTaxPct} minWidth={140} />
                <ReadOnlyField label="Fixed field OPEX / year" value={fixedFieldOpex} minWidth={180} />
              </>
            ) : (
              <>
                <TextField
                  label="Discount rate (%)"
                  size="small"
                  value={discountRatePct}
                  onChange={(e) => setDiscountRatePct(e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{ width: 160 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    label="Currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Royalty (%)"
                  size="small"
                  value={royaltyPct}
                  onChange={(e) => setRoyaltyPct(e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{ width: 140 }}
                />
                <TextField
                  label={getEconomicsTransportLabel(scenarioUnitId)}
                  size="small"
                  value={transportPerUnit}
                  onChange={(e) => setTransportPerUnit(e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{ width: 160 }}
                />
                <TextField
                  label="Income tax (%)"
                  size="small"
                  value={incomeTaxPct}
                  onChange={(e) => setIncomeTaxPct(e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{ width: 140 }}
                />
                <TextField
                  label="Fixed field OPEX / year"
                  size="small"
                  value={fixedFieldOpex}
                  onChange={(e) => setFixedFieldOpex(e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{ width: 180 }}
                />
              </>
            )}
          </Box>

          {!readOnly && mismatchedTemplateCurrencies.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Scenario currency is {currency}, but applied template
              {mismatchedTemplateCurrencies.length > 1 ? 's use' : ' uses'}{' '}
              {mismatchedTemplateCurrencies.join(', ')}. Monetary values were copied without conversion;
              adjust them manually or recalculate with caution.
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Reservoir parameters
          </Typography>

          {!readOnly && (
            <Box sx={{ mb: 2, maxWidth: 360 }}>
              <Autocomplete
                size="small"
                options={layerTemplateOptions}
                getOptionLabel={(opt) => opt.name}
                value={layerTemplateOptions.find((t) => t.id === layerTemplateId) ?? null}
                onChange={(_, value) => {
                  void applyLayerTemplate(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Layer economics template"
                    placeholder="Apply to all reservoirs…"
                  />
                )}
              />
            </Box>
          )}

          {layers.length === 0 ? (
            <Alert severity="info">Add reservoirs to the scenario before running economics.</Alert>
          ) : (
            <TableContainer sx={{ overflowX: 'auto', width: 'max-content', maxWidth: '100%' }}>
              <Table size="small" sx={economicsTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={paramColumnSx} />
                    {layers.map((layer, layerIndex) => (
                      <TableCell key={layer.scenario_layer_id} align="left" sx={layerHeaderCellSx(layerIndex)}>
                        {layer.layer_name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paramRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell sx={paramColumnSx}>{row.label}</TableCell>
                      {layers.map((layer, layerIndex) => (
                        <TableCell key={layer.scenario_layer_id} align="left" sx={layerValueCellSx(layerIndex)}>
                          {readOnly ? (
                            <Typography variant="body2" sx={{ minWidth: 80 }}>
                              {draftsByLayer[layer.scenario_layer_id]?.[row.key] || '—'}
                            </Typography>
                          ) : (
                            <TextField
                              size="small"
                              variant="outlined"
                              value={draftsByLayer[layer.scenario_layer_id]?.[row.key] ?? ''}
                              onChange={(e) => updateDraft(layer.scenario_layer_id, row.key, e.target.value)}
                              inputProps={{ inputMode: 'decimal' }}
                              sx={{ width: 100 }}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {layers.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
                Additional parameters (optional)
              </Typography>
              <TableContainer sx={{ overflowX: 'auto', width: 'max-content', maxWidth: '100%' }}>
                <Table size="small" sx={economicsTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={paramColumnSx} />
                      {layers.map((layer, layerIndex) => (
                        <TableCell key={layer.scenario_layer_id} align="left" sx={layerHeaderCellSx(layerIndex)}>
                          {layer.layer_name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {additionalParamRows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell sx={paramColumnSx}>{row.label}</TableCell>
                        {layers.map((layer, layerIndex) => (
                          <TableCell key={layer.scenario_layer_id} align="left" sx={layerValueCellSx(layerIndex)}>
                            {readOnly ? (
                              <Typography variant="body2" sx={{ minWidth: 80 }}>
                                {draftsByLayer[layer.scenario_layer_id]?.[row.key] || '—'}
                              </Typography>
                            ) : (
                              <TextField
                                size="small"
                                variant="outlined"
                                placeholder="0"
                                value={draftsByLayer[layer.scenario_layer_id]?.[row.key] ?? ''}
                                onChange={(e) => updateDraft(layer.scenario_layer_id, row.key, e.target.value)}
                                inputProps={{ inputMode: 'decimal' }}
                                sx={{ width: 100 }}
                              />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {!readOnly && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleCalculate}
                disabled={calculating || layers.length === 0}
              >
                {calculating ? 'Calculating…' : 'Calculate economics'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {result && sortedVariants.length > 0 && showSavedResults && (
        <Box>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Field
          </Typography>
          {sortedVariants.map((variantResult) => (
            <CashFlowChart
              key={`field-${variantResult.variant}`}
              title={`Field — ${variantLabel(variantResult.variant, scenarioMode)}`}
              subtitle="All reservoirs combined"
              data={variantResult.yearly}
              currency={result.currency}
              npv={variantResult.npv}
            />
          ))}

          {layerNames.map((layerName) => (
            <Box key={layerName} sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                {layerName}
              </Typography>
              {sortedVariants.map((variantResult) => {
                const layer = variantResult.layers.find((l) => l.layer_name === layerName);
                if (!layer) return null;
                return (
                  <CashFlowChart
                    key={`${layerName}-${variantResult.variant}`}
                    title={`${layerName} — ${variantLabel(variantResult.variant, scenarioMode)}`}
                    subtitle="Reservoir cash flow"
                    data={layer.yearly}
                    currency={result.currency}
                    npv={layer.npv}
                  />
                );
              })}
            </Box>
          ))}
        </Box>
      )}

    </Box>
  );
};

export default ScenarioEconomicResultsStep;
